import type { Compare, Except, OptionalRecord } from '@festina-life/core'
import cx from 'classnames'
import { type ComponentProps, useMemo } from 'react'

import type { Column, ColumnId } from '../sortableTable/createColumnDefiner'
import { SortableTable } from '../sortableTable/SortableTable'
import { mapColumnToCompare } from './mapColumnToCompare'

type Props<T> = Except<
  ComponentProps<typeof SortableTable<Compare<T>>>,
  'data' | 'columns'
> & {
  rowPairs: Compare<T>[]
  columns: Record<ColumnId, Column<T>>
  isDiffing: boolean
}

export function TableWithCompare<T extends OptionalRecord>({
  rowPairs,
  columns,
  isDiffing,
  rowClassName,
  ...restProps
}: Props<T>) {
  const getRowPairSort = (rp1: Compare<T>, rp2: Compare<T>) => {
    const rp1Val = (rp1.current ? 10 : 0) + (rp1.compare ? -5 : 0)
    const rp2Val = (rp2.current ? 10 : 0) + (rp2.compare ? -5 : 0)

    return rp2Val - rp1Val
  }

  const data = rowPairs.toSorted((rp1, rp2) => getRowPairSort(rp1, rp2))

  const compareColumns = useMemo(
    (): Record<ColumnId, Column<Compare<T>>> =>
      Object.fromEntries(
        Object.entries(columns).map(([columnId, column]) => [
          columnId,
          mapColumnToCompare({ column, isDiffing }),
        ]),
      ),
    [columns, isDiffing],
  )

  return (
    <SortableTable
      data={data}
      columns={compareColumns}
      rowClassName={(rowPair) =>
        cx(rowClassName?.(rowPair) ?? '', {
          'line-through': isDiffing && !rowPair.current && !!rowPair.compare,
        })
      }
      {...restProps}
    />
  )
}
